<template lang="pug">
  .el-container.is-vertical(v-loading="loading")
    header.main-header(:class="{show: isMenuOpen}", ref="header")
      .menu-button(:class="{close: isMenuOpen}")
        .icon(@click="handleMenuBtnClick()", :class="{close: isMenuOpen}")
      .menu-container(
        :class="{show: isMenuOpen, hide: !isMenuOpen}"
      )
        ExploreMenu(
          :articles="articles",
          @menu-item-click="goTo"
        ).menu
      .wrapper(:class="{hide: showTitle}")
        h1.text-center  Shops at {{airport.terminal}}
        ExploreMenu(
          :articles="articles",
          @menu-item-click="goTo"
        )
      .title(:class="{show: showTitle, hide: !showTitle}")
        h1 Shops at {{airport.terminal}}
    ExploreArticle(
      v-for="(article,index) in articles"
      :class="getArticleClass(article)"
      :article="article"
      :airport="airport"
      :first="index === 0"
      :index="index"
      :last="index === articles.length - 1"
      :key="index"
      @go-to-previous="goToPrevious(index)"
      @go-to-next="goToNext(index)"
      @in-viewport="inViewport"
      @go-to-stores="goToStores(index)")
</template>

<script>
import ExploreService from '@/services/ExploreService';
import ExploreArticle from '@/components/ExploreArticle';
import ExploreMenu from './ExploreMenu';

export default {
  components: { ExploreMenu, ExploreArticle },
  filters: {
    uppercase(str) {
      return str.toUpperCase();
    },
    lowercase(str) {
      return str.toLowerCase();
    },
  },
  props: {
    id: {
      default: '',
      type: String,
    },
  },
  data() {
    return {
      loading: false,
      airport: {
        id: null,
        name: '',
        code: '',
        sections: [],
        terminal: '',
      },
      articles: [
        {
          text: 'Shops',
          active: true,
        },
        {
          text: 'Dine',
        },
        {
          text: 'Services',
        },
        {
          text: 'Special Offers',
          shortText: 'Offers',
        },
      ],
      isMenuOpen: false,
      showTitle: false,
    };
  },
  beforeMount() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleScroll);
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.loading = true;
      ExploreService.getAirport(this.id)
        .then(({ data }) => {
          this.airport = Object.assign({}, this.airport, data);
        })
        .catch(error => {
          this.$message({
            type: 'error',
            message: error.response
              ? error.response.data.errorMessage
              : error.message,
          });
          this.$router.push({ name: 'ExploreDashboard' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleScroll() {
      const root = document.documentElement;
      this.setDefaultProperties(root);
      if (window.innerWidth <= 700 ||
        window.innerWidth > window.innerHeight && window.innerWidth <= 825) {
        this.setHeaderProperties(root);
      }
    },
    setDefaultProperties(root) {
      root.style.setProperty('--main-header-top', '0px');
      root.style.setProperty('--menu-button-translateY', '0px');
      root.style.setProperty('--wrapper-opacity', '1');
    },
    setHeaderProperties(root) {
      const y = window.scrollY;
      const pos = this.getPositions();
      root.style.setProperty('--main-header-top', `${Math.max(-pos.elHeight, -y)}px`);
      root.style.setProperty('--menu-button-translateY', `${Math.min(y, pos.buttonY)}px`);
      this.updateTitle(root, pos, y);
    },
    updateTitle(root, pos, scrollY) {
      const delta = scrollY / pos.elHeight;
      const wrapperOpacity = Math.max(1 - delta, 0);
      root.style.setProperty('--wrapper-opacity', `${wrapperOpacity}`);
      this.showTitle = !wrapperOpacity;
    },
    getPositions() {
      const headerHeight = this.$refs.header.getClientRects()[0].height;
      if ((window.innerWidth <= 700 && window.innerWidth > 600) ||
        (window.innerWidth > window.innerHeight && window.innerWidth <= 825
          && window.innerWidth > 700)) {
        return { elHeight: headerHeight - 60, buttonY: headerHeight - 62 };
      } else if (window.innerWidth <= 600 && window.innerWidth > 400) {
        return { elHeight: headerHeight - 50, buttonY: headerHeight - 54 };
      }
      return { elHeight: headerHeight - 40, buttonY: headerHeight - 44 };
    },
    getArticleClass(article) {
      return (article.shortText || article.text).toLowerCase();
    },
    goToPrevious(index) {
      if (index === 0) {
        return;
      }
      this.goTo(index - 1);
    },
    goToNext(index) {
      if (index === this.articles.length - 1) {
        return;
      }
      this.goTo(index + 1);
    },
    goTo(index) {
      const elClass = this.getArticleClass(this.articles[index]);
      const el = document.querySelector(`article.${elClass}`).offsetTop;
      const firstEl = document.getElementsByTagName('header')[0].offsetHeight;
      window.scrollTo({
        top: el - firstEl,
        left: 0,
        behavior: 'smooth',
      });
      if ((window.innerWidth < 825 && window.innerWidth > window.innerHeight)
        || window.innerWidth < 700) {
        this.inViewport(index);
      }
    },
    inViewport(index) {
      this.articles = this.articles.map(article => {
        const newArticle = article;
        newArticle.active = false;
        return newArticle;
      });

      this.articles[index].active = true;
    },
    goToStores(index) {
      this.$router.push({
        name: 'ExploreAirportStores',
        params: { id: this.id },
        query: { index },
      });
    },
    handleMenuBtnClick() {
      this.isMenuOpen = !this.isMenuOpen;
    },
  },
};
</script>

<style lang="stylus" scoped>
  @import url('../../static/fonts/rift/stylesheet')
  @import url('../../static/fonts/futura/stylesheet')

  *
    font-family 'Rift Soft'

  .main-header
    height var(--main-header-height)
    color #fff
    position fixed
    background-image url('../../static/images/main-bg.jpg')
    background-size cover
    width 100%
    box-sizing border-box
    z-index 10
    display flex
    align-items center
    justify-content center

  .menu-button
    display none
    width var(--menu-button-width)
    height var(--menu-button-height)
    position absolute
    top var(--menu-button-top)
    left var(--menu-button-left)
    z-index 10
    transform translateY(var(--menu-button-translateY))

    &.close
      position fixed
      transform translateY(0px)
      width var(--menu-close-button-width)
      height var(--menu-close-button-height)
      top var(--menu-close-button-top)
      left var(--menu-close-button-left)

  .icon
    width 100%
    height 100%
    background-size cover
    background-image url("data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' data-prefix='fas' data-icon='bars' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' class='svg-inline--fa fa-bars fa-w-14 fa-3x'%3E%3Cpath fill='white' d='M16 132h416c8.837 0 16-7.163 16-16V76c0-8.837-7.163-16-16-16H16C7.163 60 0 67.163 0 76v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16zm0 160h416c8.837 0 16-7.163 16-16v-40c0-8.837-7.163-16-16-16H16c-8.837 0-16 7.163-16 16v40c0 8.837 7.163 16 16 16z' class=''%3E%3C/path%3E%3C/svg%3E")
    &.close
      background-image url("data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' data-prefix='fal' data-icon='times' role='img' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512' class='svg-inline--fa fa-times fa-w-10 fa-3x'%3E%3Cpath fill='white' d='M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z' class=''%3E%3C/path%3E%3C/svg%3E")

  .title
    position absolute
    top var(--title-top)
    opacity 0
    transition opacity 0.3s
    border 1px solid rgba(255, 255, 255, 0.5)
    width 98%
    height var(--title-height)
    display none

    h1
      text-align right
      line-height var(--title-h1-line-height)
      margin-right var(--title-h1-margin-right)
      font-size var(--title-h1-font-size)

  .wrapper
    border 1px solid rgba(255,255,255,0.5)
    width 99%
    opacity var(--wrapper-opacity)
    height var(--wrapper-height)
    display flex
    flex-direction column
    align-items center
    justify-content space-evenly

    .text-center
      text-transform uppercase
      padding 0
      margin 0
      font-weight 300
      font-size var(--wrapper-text-font-size)

  .menu-container
    position absolute
    width 100%
    background-color black
    height var(--main-header-height)
    opacity 0
    z-index 9
    top  calc(0px - var(--main-header-height) - var(--main-header-top))
    transition transform 0.3s, opacity 0.3s

@media screen and (max-width: 825px) and (orientation: landscape)
  .main-header
    top var(--main-header-top)

  .title
  &.show
    display block
    opacity 1

  &.hide
    display none
    opacity 0

  .wrapper
    position relative
    width 98%
    .text-center
      font-size var(--wrapper-text-font-size)
      line-height var(--wrapper-text-line-height)

    &.hide
      display none

    &.show
      background-image url('')
      background-color black

  .menu-button
    display block

  .menu-button:hover
    cursor pointer

  .menu
    display none

  .menu-container
    .menu
      display block
      margin 0
      margin-top var(--menu-margin-top)

    &.show
      transform translateY(var(--main-header-height))
      opacity 1

    &.hide
      transform translateY(0)
      opacity 0


@media screen and (max-width 700px)

    .main-header
      top var(--main-header-top)
      display flex
      align-items center
      justify-content center
      padding 0

    .title
    &.show
      display block
      opacity 1

    &.hide
      opacity 0

    .wrapper
      position relative
      width 98%

      &.hide
        display none

      &.show
        background-image url('')
        background-color black

    .menu-button
      display block

    .menu-button:hover
      cursor pointer

    .menu
      display none

    .menu-container
      .menu
        display block
        margin 0
        margin-top var(--menu-margin-top)

      &.show
        transform translateY(var(--main-header-height))
        opacity 1

      &.hide
        transform translateY(0)
        opacity 0

@media screen and (max-width 400px)
    .wrapper
      width 96.5%

    .title
      width 96.5%

</style>



// WEBPACK FOOTER //
// src/components/ExploreAirport.vue